/**
 * All hardcoded copy strings for the MetricsNarrativeChart insight notice.
 *
 * Each view classifies into one sentiment — growing / steady / declining — and
 * shows the matching observation. Copy is observation-only (no recommendations:
 * the chart sometimes shows posting consistently didn't drive growth, and real
 * recommendations belong to the LLM-backed Takeaways feature) and is scoped to
 * the period being viewed:
 *
 *  - Single views (This Period / Comparison) describe the displayed line's
 *    within-period movement → `thisPeriod` copy ("…this period"). Level metrics
 *    only; count metrics have no within-period story.
 *  - The Both view compares this period to the comparison period → `comparison`
 *    copy ("…than last period").
 *
 * Edit this file to change wording. Selection logic lives in selectInsight.ts.
 */

export interface SentimentMessages {
  /** The view's primary metric is trending up. */
  growing: string
  /** No meaningful change. */
  steady: string
  /** The view's primary metric is trending down. */
  declining: string
}

export interface ViewSentimentCopy {
  /**
   * Within-period observation for single views (This Period / Comparison).
   * Present only for level metrics — count metrics have no within-period story
   * and show no notice in single views.
   */
  thisPeriod?: SentimentMessages
  /** Period-over-period observation for the Both view ("…than last period"). */
  comparison: SentimentMessages
}

/**
 * Per-view (and per-single-metric) copy. Keyed by the narrative view key
 * (content-impact, audience-growth, visibility) or, on the Posts tab, the
 * selected metric key (posts, followers, followers-net-new, views, reach).
 *
 * Each view leads with what makes it distinct: posts → cadence; content-impact
 * → the relationship between activity and audience; audience-growth → the
 * trajectory; visibility → getting discovered. Observations never claim posting
 * *caused* a result, so they can't contradict the data.
 */
export const VIEW_SENTIMENT_MESSAGES: Record<string, ViewSentimentCopy> = {
  'content-impact': {
    thisPeriod: {
      growing: 'Your content impact grew this period.',
      steady: 'Your content impact held steady this period.',
      declining: 'Your content impact declined this period.',
    },
    comparison: {
      growing: 'Your content impact grew more than last period.',
      steady: 'Your content impact was the same as last period.',
      declining: 'Your content impact grew less than last period.',
    },
  },
  'audience-growth': {
    thisPeriod: {
      growing: 'Your audience grew this period.',
      steady: 'Your audience held steady this period.',
      declining: 'Your audience declined this period.',
    },
    comparison: {
      growing: 'Your audience grew more than last period.',
      steady: 'Your audience grew as much as last period.',
      declining: 'Your audience grew less than last period.',
    },
  },
  followers: {
    thisPeriod: {
      growing: 'Your follower count grew this period.',
      steady: 'Your follower count held steady this period.',
      declining: 'Your follower count dipped this period.',
    },
    comparison: {
      growing: 'Your follower count grew more than last period.',
      steady: 'Your follower count grew as much as last period.',
      declining: 'Your follower count grew less than last period.',
    },
  },
  'followers-net-new': {
    thisPeriod: {
      growing: 'You gained net new followers this period.',
      steady: 'Your net new followers held steady this period.',
      declining: 'You lost more followers than you gained this period.',
    },
    comparison: {
      growing: 'You gained more net new followers than last period.',
      steady: 'You gained as many net new followers as last period.',
      declining: 'You gained fewer net new followers than last period.',
    },
  },
  posts: {
    comparison: {
      growing: 'You posted more than last period.',
      steady: 'You posted the same as last period.',
      declining: 'You posted less than last period.',
    },
  },
  views: {
    comparison: {
      growing: 'You got more profile views than last period.',
      steady: 'You got the same profile views as last period.',
      declining: 'You got fewer profile views than last period.',
    },
  },
  reach: {
    comparison: {
      growing: 'You reached more people than last period.',
      steady: 'You reached the same number of people as last period.',
      declining: 'You reached fewer people than last period.',
    },
  },
  visibility: {
    comparison: {
      growing: 'More people discovered you than last period.',
      steady: 'Just as many people discovered you as last period.',
      declining: 'Fewer people discovered you than last period.',
    },
  },
}

/**
 * Volume count metrics (Profile Views, Reach) spike on activity days and have no
 * within-period growing/steady/declining story. In single views (This Period /
 * Comparison) they report their peak instead — keyed by the metric, given the
 * formatted peak value and date. The Both view still uses the `comparison` copy
 * above. (Posts has no peak template: single views show its period total via
 * {@link postsTotalMessage}; the Both view stays comparative.)
 */
export const PEAK_MESSAGES: Record<
  string,
  (value: string, date: string) => string
> = {
  views: (value, date) => `Your profile views peaked at ${value} on ${date}.`,
  reach: (value, date) => `Your reach peaked at ${value} on ${date}.`,
}

/**
 * Posts in single views: a plain volume statement for the displayed period.
 * "…than last period" is meaningless when only one period is on screen (and is
 * circular in the Comparison view), so single views just state how much was
 * posted. The Both view keeps the comparative copy in `VIEW_SENTIMENT_MESSAGES`.
 */
export function postsTotalMessage(count: number): string {
  return `You published ${count} ${count === 1 ? 'post' : 'posts'} this period.`
}

/**
 * Follower Growth Rate (benchmark) view. Inherently comparative — the channel's
 * growth rate against the median for similar accounts — so it has its own
 * above/below pair rather than the growing/steady/declining sentiments.
 */
export const BENCHMARK_MESSAGES = {
  above:
    'Your audience growth is above the median for similar accounts, and accelerating.',
  below: 'Your audience growth is below the median for similar accounts.',
}
